import { splitSuggestions } from '@/functions/documentSuggestions'

type SupportedLang = 'fr' | 'de' | 'en' | 'es'

const DOSE_LOCALE: Record<SupportedLang, { during: string; day: string; days: string; asNeeded: string; dayAbbrev: string }> = {
  fr: { during: 'pendant', day: 'jour', days: 'jours', asNeeded: 'si besoin', dayAbbrev: 'j' },
  de: { during: 'während', day: 'Tag', days: 'Tagen', asNeeded: 'bei Bedarf', dayAbbrev: 'd' },
  en: { during: 'for', day: 'day', days: 'days', asNeeded: 'if needed', dayAbbrev: 'd' },
  es: { during: 'durante', day: 'día', days: 'días', asNeeded: 'si es necesario', dayAbbrev: 'd' },
}

// CREATE DOSE OBJECT

export interface ParsedDose {
  text: string
  days: string
  asNeeded: boolean
}

/**
 * UI → DTO
 */
export function toDoseDto(dose: ParsedDose) {
  return {
    posologie: dose.text.trim(),
    days: dose.days ? parseInt(dose.days) : null,
    asNeeded: dose.asNeeded ?? false,
  }
}

/**
 * Empty parsed dose for new medications
 */
export function emptyParsedDose(): ParsedDose {
  return { text: '', days: '', asNeeded: false }
}

/**
 * Builds a doses array from a list of doseTimings, scoped to the most recent expression
 * and filtered to active/upcoming doses relative to currEntryDate.
 */
export function buildDosesFromDoseTimings(doseTimings: any[], currEntryDate: string): ParsedDose[] {
  const sorted = [...doseTimings].sort((a, b) => {
    const tA = new Date(a.timelineStart).getTime()
    const tB = new Date(b.timelineStart).getTime()
    if (tA - tB !== 0) return tA - tB
    return a.sequenceOrder - b.sequenceOrder
  })
  const latestExprId = sorted[sorted.length - 1]?.expressionId
  const fromLatest = sorted.filter(dt => dt.expressionId === latestExprId)
  const firstActiveIdx = fromLatest.findIndex(dt =>
    !dt.timelineEnd || new Date(dt.timelineEnd) > new Date(currEntryDate)
  )
  const active = firstActiveIdx === -1 ? fromLatest.slice(-1) : fromLatest.slice(firstActiveIdx)
  return active.map((dt, i) => {
    let days: string = dt.days != null ? String(dt.days) : ''
    if (i === 0 && dt.timelineEnd) {
      const remaining = Math.ceil((new Date(dt.timelineEnd).getTime() - new Date(currEntryDate).getTime()) / (1000 * 60 * 60 * 24))
      days = remaining > 0 ? String(remaining) : ''
    }
    return { text: dt.originalPosologie, days, asNeeded: dt.asRequired }
  })
}

/**
 * Parses a single dose string into a structured dose object.
 * Two parsing strategies, checked in order:
 *
 * 1. Parenthetical metadata at end of line: (...)\s*$
 *    Recognized patterns inside parentheses:
 *    - Days: a number optionally followed by d/j/t → (10d), (7j), (5t), (10)
 *    - As needed: a known keyword → besoin, bedarf, needed, necesario, PRN
 *    - Combined: (10d si besoin), (7j bei Bedarf), (PRN)
 *    Safety rules:
 *    - If nothing inside is recognized → parentheses kept as part of text
 *    - If days found but leftover text isn't a known keyword → likely typo,
 *      parentheses kept as part of text (e.g. "10d si besoon" stays intact)
 *
 * 2. Legacy French free text (no parentheses):
 *    - "si besoin" anywhere → asNeeded
 *    - "pendant N jours" → days
 *    TODO: remove when all legacy data migrated
 */
function parseSingleDose(text: string): ParsedDose {
  const phase = text.trim()

  // 1. Check for parenthetical metadata at end of line
  const metaMatch = phase.match(/\(([^)]+)\)\s*$/)

  if (metaMatch) {
    const inner = metaMatch[1].trim()

    // Extract days: number + optional day letter (d/j/t)
    const daysMatch = inner.match(/^(\d+)[djt]?\b/i) || inner.match(/(\d+)[djt]?\s*$/i)
    const days = daysMatch ? daysMatch[1] : ''

    // Whatever remains after stripping the days portion
    const rest = inner.replace(/\d+[djt]?\s*/i, '').trim()
    const asNeeded = /besoin|bedarf|needed|necesario|PRN|SOS|n\.?b\.?/i.test(rest)

    // Nothing recognized → preserve parentheses as part of posology
    if (!days && !asNeeded) return { text: phase, days: '', asNeeded: false }

    // Days found but unrecognized leftover → likely typo, preserve everything
    if (days && !asNeeded && rest.length > 0) return { text: phase, days: '', asNeeded: false }

    const posologie = phase.slice(0, metaMatch.index).trim()
    return { text: posologie, days, asNeeded }
  }

  // 2. Legacy French format (no parentheses)
  const asNeeded = /si besoin/i.test(phase)
  const cleaned = phase.replace(/si besoin/i, '').trim()
  const daysMatch = cleaned.match(/(?:pendant|pour)\s+(\d+)\s+jours?\.?/i)
  const days = daysMatch ? daysMatch[1] : ''
  const txt = cleaned.replace(/(?:pendant|pour)\s+\d+\s+jours?\.?/i, '').replace(/:?\s*$/, '').trim()
  return { text: txt, days, asNeeded }
}

/**
 * Parses a favorite string into dose rows.
 * Delegates splitting to splitSuggestions():
 *   - No "---" → each line is one dose phase
 *   - "---" present → each block between separators is one dose phase
 */
export function parseFavorite(text: string): ParsedDose[] {
  if (!text) return [emptyParsedDose()]
  const blocks = splitSuggestions(text)
  if (!blocks.length) return [emptyParsedDose()]
  return blocks.map((block: { text: string }) => parseSingleDose(block.text))
}

// DOSE OBJECT -> PARSED STRING

/**
 * Builds a favorite string from a doses array.
 * Phases joined by newlines. Metadata in parentheses using localized abbreviations.
 * e.g. (fr) [{ text: '1000mg 1-1-1', days: '10', asNeeded: true }]
 * becomes "1000mg 1-1-1 (si besoin 10j)"
*/
export function buildFavoriteString(doses: ParsedDose[], lang = 'fr'): string {
  const locale = DOSE_LOCALE[lang as SupportedLang] ?? DOSE_LOCALE.fr
  return doses
    .filter(d => d.text.trim())
    .map(d => {
      let str = d.text.trim()
      if (d.days && d.asNeeded) str += ` (${locale.asNeeded} ${d.days}${locale.dayAbbrev})`
      else if (d.days) str += ` (${d.days}${locale.dayAbbrev})`
      else if (d.asNeeded) str += ` (${locale.asNeeded})`
      return str
    })
    .join('\n')
}

// DOSE OBJECT -> NATURAL LANGUAGE (STRING)

/**
 * Returns the first phase of a favorite string, formatted in natural language.
 * "1000mg 1-1-1 (si besoin 10j)" becomes "1000mg 1-1-1 si besoin pendant 10 jours."
*/
export function formatFavoriteFirstPhase(text: string, lang = 'fr'): string {
  if (!text) return ''
  const parsed = parseFavorite(text)[0]
  return formatDoseToNaturalLanguage(parsed, lang)
}

/**
 * Returns a suffix indicating additional phases, or empty string if single phase.
 * e.g. "60mg (30d)\n---\n40mg (20d)\n---\n20mg (10d)" → "+2"
 */
export function formatFavoriteSuffix(text: string): string {
  if (!text) return ''
  const blocks = splitSuggestions(text)
  return blocks.length > 1 ? `+${blocks.length - 1}` : ''
}

/**
 * Formats a single dose object into a natural language string.
 */
export function formatDoseToNaturalLanguage(dose: ParsedDose, lang = 'fr'): string {
  const locale = DOSE_LOCALE[lang as SupportedLang] ?? DOSE_LOCALE.fr
  let str = dose.text.trim()
  if (!str) return ''
  if (dose.asNeeded) str += ` ${locale.asNeeded}`
  if (dose.days) {
    const dayWord = parseInt(dose.days) === 1 ? locale.day : locale.days
    str += ` ${locale.during} ${dose.days} ${dayWord}.`
  }
  return str
}

/**
 * Serializes a doses array to a multiline natural language string.
 */
export function serializeDoses(doses: ParsedDose[], lang = 'fr'): string {
  return doses
    .map(d => formatDoseToNaturalLanguage(d, lang))
    .filter(s => s)
    .join('\n')
}

